import { useEffect, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'

const STATE = {
  LOADING: 'loading',
  FETCHED: 'fetched',
  ERROR: 'error',
}
const useFetch = (url, options) => {
  const cache = useRef({})
  const cancelRequest = useRef(false)

  const initialState = {
    error: undefined,
    data: undefined,
  }

  const fetchReducer = (state, action) => {
    switch (action.type) {
      case STATE.LOADING:
        return { ...initialState, status: STATE.LOADING }
      case STATE.FETCHED:
        return { ...initialState, status: STATE.FETCHED, data: action.payload }
      case STATE.ERROR:
        return { ...initialState, status: STATE.ERROR, error: action.payload }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(fetchReducer, initialState)

  useEffect(() => {
    /* Do nothing if url is not given */
    if (!url || !url.trim()) return

    const fetchData = async () => {
      dispatch({ type: STATE.LOADING })

      /* use cache if existed */
      if (cache.current[url]) {
        dispatch({ type: STATE.FETCHED, payload: cache.current[url] })
        return
      }

      try {
        /* fetch data */
        const response = await fetch(
          `${encodeURI(url)}`,
          omitBy(
            {
              headers: omitBy(
                {
                  'Content-Type': 'application/json; charset=utf-8',
                  accessToken: Cookies.get('accessToken'),
                },
                isNil,
              ),
              ...options,
            },
            isNil,
          ),
        )

        if (!response.ok) {
          throw new Error(response.statusText)
        }

        const data = await response.json()
        cache.current[url] = data

        if (cancelRequest.current) return

        dispatch({ type: STATE.FETCHED, payload: data })
      } catch (error) {
        if (cancelRequest.current) return

        dispatch({ type: STATE.ERROR, payload: error })
      }
    }

    fetchData()

    return () => {
      cancelRequest.current = true
    }
  }, [url, options])

  return state
}

useFetch.defaultProps = {
  options: {},
}

useFetch.propTypes = {
  url: PropTypes.string.isRequired,
  options: PropTypes.object,
}

export default useFetch
